import React from 'react';
import { useRouter } from 'next/compat/router';
import { firstValueFrom } from 'rxjs';
import {
  useCurrentTierData,
  useCurrentUserData,
} from '@pass-frontend/common/data-bridge';
import { AdvantageProfile } from '@pass-frontend/data-access/common/edge';
import { ArcadeProfile } from '@pass-frontend/features/chrome/data-access';
import { env } from '@pass-frontend/features/configuration/util';
import { eventBus, createEvent } from '@pass-frontend/utility/event-bus';
import { useStableCallback } from '@pass-frontend/utility/swiss-knife';
import { getPageName, logAnalyticsEvent } from './common-analytics.service';
import { ANALYTICS_REQUEST } from './events';
import {
  AnalyticsUserProperties,
  EventType,
  userProperties,
  PageTags,
} from './types';

/* eslint-disable-next-line */
export interface CommonAnalyticsProps {}

let userPropertiesInfo = {};

export const ADVANTAGE_USER_FETCHED = createEvent<AdvantageProfile>(
  'advantage/user/fetched',
);

export const ARCADE_USER_FETCHED = createEvent<ArcadeProfile>(
  'arcade/user/fetched',
);

export const getUserProperties = () =>
  getTrackingUserProperties(userPropertiesInfo);

export const setUserProperties = (userData: any) => {
  if (env().PRODUCT_NAME === 'renew-active') {
    if (userData?.currentUser?.lineOfBusiness || userData?.lineOfBusiness) {
      userPropertiesInfo = {
        ...userPropertiesInfo,
        ...userData,
        lineOfBusiness:
          userData?.currentUser?.lineOfBusiness || userData?.lineOfBusiness,
        key: 'Government',
      };
    }
  } else {
    userPropertiesInfo = {
      ...userData,
      ...userPropertiesInfo,
    };
  }
};

export const getTrackingUserProperties = (
  props: any,
): AnalyticsUserProperties => {
  return {
    [userProperties.rallyId]: props?.rallyId,
    [userProperties.client]: props?.client,
    [userProperties.partner]: props?.partner,
    [userProperties.sessionId]: props?.sessionId,
    [userProperties.subscriptionTierId]: props?.tierId,
    [userProperties.subscriptionTierName]: props?.name,
    [userProperties.product]: env().PRODUCT_NAME,
    [userProperties.serviceSector]: props.key,
    [userProperties.lineOfBusiness]: props?.lineOfBusiness,
    [userProperties.HSID]: props?.hsId,
    [userProperties.EID]: props?.eid,
  };
};

eventBus.listen(ANALYTICS_REQUEST.match, (e) => {
  logAnalyticsEvent(EventType.PageNav, e.payload);
});

const userData =
  env().PRODUCT_NAME == 'renew-active'
    ? firstValueFrom(eventBus.query(ARCADE_USER_FETCHED.match))
    : {};

// FIXME: we need to consider the sceanrio when calling from the renew active product
export function CommonAnalytics(props: CommonAnalyticsProps) {
  const router = useRouter();
  const { currentTier, loading: isLoadingTierData } = useCurrentTierData();
  const {
    currentSponsor,
    currentMember,
    loading: isLoadingUserData,
  } = useCurrentUserData();

  React.useEffect(() => {
    if (
      currentSponsor?.clientNameAffiliations &&
      currentSponsor?.clientNameAffiliations !== null
    ) {
      userPropertiesInfo = {
        ...userPropertiesInfo,
        client:
          currentSponsor?.clientNameAffiliations[0]?.rallyClientUniqueName,
        partner: currentSponsor?.clientNameAffiliations[0]?.partner,
      };
    }

    setUserProperties({
      ...currentTier,
      ...currentSponsor?.serviceSector,
      ...currentMember,
    });
  }, [
    currentTier?.tierId,
    currentSponsor?.serviceSector?.key,
    currentMember?.lineOfBusiness,
  ]);

  const logPageLoadEvent = useStableCallback(async function (url: string) {
    let pageName = '';
    if (
      (url === '/' || url.includes('?cid=') || url.startsWith('/faqs')) &&
      currentMember === undefined
    ) {
      const link = window.location.href;
      if (
        link.includes('onepassselect') ||
        link.includes('pass-standalone-commercial')
      ) {
        pageName = 'op-commercial-landing';
      } else {
        pageName = 'op-government-landing';
      }

      if (url.startsWith('/faqs')) {
        pageName = getPageName(url);
      }
    } else {
      pageName = getPageName(url);
      const arcadeUser = await userData;
      const userDetails = {
        ...arcadeUser,
      };
      setUserProperties(userDetails);
    }
    eventBus.trigger(
      ANALYTICS_REQUEST({
        pageName,
        pageTags: {
          [PageTags.NewPageLoad]: true,
        },
      }),
    );
  });

  React.useEffect(() => {
    router?.events.on('routeChangeComplete', logPageLoadEvent);
    return () => router?.events.off('routeChangeComplete', logPageLoadEvent);
  }, []);

  // Send an event on a first-time load / server-refresh as well
  React.useEffect(() => {
    if (!isLoadingUserData && !isLoadingTierData && router.isReady) {
      logPageLoadEvent(router?.asPath);
    }
  }, [isLoadingUserData, isLoadingTierData]);
  return null;
}

export default CommonAnalytics;
